import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges, ViewChild } from '@angular/core';
import { cleanHtmlTags } from '@emma-helpers/emma-utils';
import { ellipsis, formatStringCase } from '@emma-helpers/format.helper';
import { QRCodeComponent } from 'angularx-qrcode';
import { get } from 'lodash';
import moment from 'moment';

import {
  COLUMN_DATA_TYPE,
  formatDataTypeCurrency,
  formatDataTypeList,
  formatDataTypeOption,
  formatDataTypeOptionList,
  formatDataTypeParams,
} from './emma-table.helpers';
import { formatDataTypeTimeWindow } from './emma-table.i18n';
import { CellValuePercentage, EMMATableColumn } from './emma-table.types';
import { TIME_UNIT } from 'emma-common-ts';

@Component({
  selector: '[emma-table-row-cell]',
  templateUrl: './emma-table-row-cell.component.html',
  standalone: false,
})
export class EMMATableRowCellComponent implements OnChanges {
  @ViewChild('QR', { static: false }) qr!: QRCodeComponent;

  @Output() $rowCellActionEvent = new EventEmitter<number>();
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  @Input() value: any;
  @Input() column!: EMMATableColumn;
  @Input() isEditable = false;

  formatDataTypeParams = formatDataTypeParams;
  // Avoid re-renders
  cachedValue = '';
  cachedTitle = '';
  DATA_TYPE = COLUMN_DATA_TYPE;

  ngOnChanges(changes: SimpleChanges): void {
    if (('value' in changes || 'column' in changes) && this.column) {
      if (this.value === undefined) {
        this.cachedValue = '';
        this.cachedTitle = '';
      } else if (this.value === null) {
        this.cachedValue = '-';
        this.cachedTitle = '';
      } else {
        this.updateCellValue();
      }
    }
  }

  private updateCellValue() {
    switch (this.column.dataType) {
      case COLUMN_DATA_TYPE.TIME_WINDOW:
        this.cachedValue = formatDataTypeTimeWindow(this.value, this.column.unit as TIME_UNIT);
        break;
      case COLUMN_DATA_TYPE.LIST:
        this.cachedValue = formatDataTypeList(this.value, this.column.listSeparator, this.column.dataKey);
        this.cachedTitle = formatDataTypeList(this.value);
        break;
      case COLUMN_DATA_TYPE.LIST_LENGTH:
        this.cachedValue = this.value?.length || 0;
        this.cachedTitle = formatDataTypeList(this.value);
        break;
      case COLUMN_DATA_TYPE.PARAMS:
        this.cachedValue = formatDataTypeParams(this.value);
        break;
      case COLUMN_DATA_TYPE.CURRENCY:
        this.cachedValue = formatDataTypeCurrency(this.value, this.column.unit);
        break;
      case COLUMN_DATA_TYPE.STRING:
        this.cachedValue = this.value;
        if (this.column.stringCase) {
          this.cachedValue = formatStringCase(this.value, this.column.stringCase);
        }
        this.cachedTitle = this.cachedValue;
        break;
      case COLUMN_DATA_TYPE.HTML:
        this.cachedTitle = cleanHtmlTags(this.value);
        break;
      case COLUMN_DATA_TYPE.DATE:
        this.cachedValue = this.value;
        if (this.value && (this.column.dateInputFormat || this.column.dateOutputFormat)) {
          const date = moment(this.value, this.column.dateInputFormat);
          this.cachedValue = date.format(this.column.dateOutputFormat);
        }
        break;
      case COLUMN_DATA_TYPE.OPTION:
      case COLUMN_DATA_TYPE.ICON_OPTION:
        this.cachedValue = formatDataTypeOption(
          this.value,
          this.column.options || [],
          this.column.optionValueKey,
          this.column.optionLabelKey
        );
        break;
      case COLUMN_DATA_TYPE.OPTION_LIST:
        this.cachedValue = formatDataTypeOptionList(
          this.value,
          this.column.options || [],
          this.column.listSeparator,
          this.column.optionValueKey,
          this.column.optionLabelKey
        );
        break;
      case COLUMN_DATA_TYPE.QR_CODE:
        this.readQrSrc();
        break;
      case COLUMN_DATA_TYPE.ACTION:
        this.cachedValue = this.value;
        break;
      case COLUMN_DATA_TYPE.PRIORITY:
        this.cachedValue = this.value;
        break;
    }
    if (this.cachedValue && this.column.maxLength) {
      this.cachedValue = ellipsis(this.cachedValue, this.column.maxLength);
    }
  }

  private updateQrCachedValue() {
    this.cachedValue = get(this.qr, 'qrcElement.nativeElement.children[0].src');
    if (!this.cachedValue) {
      this.readQrSrc();
    }
  }

  private readQrSrc() {
    setTimeout(() => this.updateQrCachedValue(), 10);
  }

  onActionEvent(value?: number): void {
    this.$rowCellActionEvent.emit(value);
  }

  getPercentageBadgeClass(value: CellValuePercentage | null): string {
    if (value?.percentage === null) {
      return '';
    }

    const percentage = value?.percentage ? value.percentage : 0;

    if (percentage >= 0.8) {
      return 'text-success bg-success';
    } else if (percentage >= 0.4) {
      return 'text-warning bg-warning';
    } else {
      return 'text-danger bg-danger';
    }
  }

  isArray(value: unknown): boolean {
    return Array.isArray(value);
  }
}
